// Alpine-Komponente für den Marker-Set-Vergleich.
// State + Darstellung (via Alpine-Bindings im Liquid-Markup) leben hier;
// die eigentliche Vergleichs-Mathematik kommt komplett aus der
// framework-freien, getesteten lgt-compare-logic.js.
//
// Cart-Integration folgt exakt dem bestehenden Theme-Mechanismus
// (siehe assets/product-quick-buy.js): fetch gegen theme.routes.cart_add_url
// mit sections-Parameter, danach das Event shapes:modalcart:afteradditem.
// fetchConfigDefaults() kommt aus dem global geladenen assets/utils.js.

import {
  buildCollection,
  compareToTarget,
  rankSets,
  findSingleSetOnlyMarkers,
  suggestAlternative,
  findBundleAdvantage,
  hexToHue,
  isLightColor,
  interpolate,
  formatPlural,
  collectorLevelProgress,
  COLLECTOR_LEVELS,
} from './lgt-compare-logic.js';
import { readStoredSelection, writeStoredSelection } from './lgt-selection-storage.js';

const FEW_NEW_COLORS_THRESHOLD = 3;

document.addEventListener('alpine:init', () => {
  Alpine.data('lgtMarkerCompare', (config) => ({
    sets: config.sets || [],
    settings: config.settings || {},
    strings: config.strings || {},

    ownedHandles: [],
    compareHandle: null,
    explorerSearch: '',
    explorerSort: 'number',
    expandedExplorerHandle: null,
    showAlreadyHave: false,
    cartLoadingHandle: null,
    cartMessage: '',
    _singleSetOnlyCache: null,

    init() {
      this.sets = [...this.sets]
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map((set) => ({ ...set, markerNumbers: set.markers.map((m) => m.number) }));
      this.restoreSelection();

      this.$watch('ownedHandles', () => this.onSelectionChange());
      this.$watch('compareHandle', () => this.onSelectionChange());
    },

    onSelectionChange() {
      this.persistSelection();
      if (this.compareHandle) {
        document.dispatchEvent(
          new CustomEvent('lgt:compare', {
            detail: {
              owned: [...this.ownedHandles],
              compare: this.compareHandle,
              result: this.comparisonResult,
            },
          })
        );
      }
    },

    restoreSelection() {
      const validHandles = new Set(this.sets.map((s) => s.handle));
      const selection = readStoredSelection(validHandles);
      this.ownedHandles = selection.owned;
      this.compareHandle = selection.compare;
    },

    persistSelection() {
      writeStoredSelection({ owned: this.ownedHandles, compare: this.compareHandle });
    },

    get setsByHandle() {
      return new Map(this.sets.map((s) => [s.handle, s]));
    },

    get ownedSets() {
      return this.sets.filter((s) => this.ownedHandles.includes(s.handle));
    },

    get notOwnedSets() {
      return this.sets.filter((s) => !this.ownedHandles.includes(s.handle));
    },

    get collection() {
      return buildCollection(this.ownedSets);
    },

    get collectionCount() {
      return this.collection.size;
    },

    get compareSet() {
      return this.setsByHandle.get(this.compareHandle) || null;
    },

    get comparisonResult() {
      if (!this.compareSet) return null;
      return compareToTarget(this.collection, this.compareSet);
    },

    get rankingCandidates() {
      return this.notOwnedSets.filter((s) => s.handle !== this.compareHandle);
    },

    get ranking() {
      return rankSets(this.collection, this.rankingCandidates);
    },

    get topRanking() {
      const limit = this.settings.rankingCount || 3;
      return this.ranking.slice(0, limit);
    },

    get collectorLevel() {
      return collectorLevelProgress(this.collection, this.sets);
    },

    get collectorLevelName() {
      const level = this.collectorLevel.current;
      return level ? this.strings.levels[level.key] : '';
    },

    get collectorLevelUntilNextText() {
      const { next, colorsUntilNext } = this.collectorLevel;
      if (!next) return '';
      const nextName = this.strings.levels[next.key] || '';
      return formatPlural(this.strings.levelUntilNext, colorsUntilNext, { level: nextName });
    },

    get levelSegments() {
      const currentKey = this.collectorLevel.current ? this.collectorLevel.current.key : null;
      const currentIndex = currentKey ? COLLECTOR_LEVELS.findIndex((l) => l.key === currentKey) : -1;
      return COLLECTOR_LEVELS.map((level, index) => {
        let fillPercent = 0;
        if (index < currentIndex) fillPercent = 100;
        else if (index === currentIndex) fillPercent = this.collectorLevel.intraLevelPercent;
        return {
          key: level.key,
          label: this.strings.levels[level.key] || '',
          fillPercent,
          isCurrent: index === currentIndex,
          isReached: index <= currentIndex,
        };
      });
    },

    get gapCloser() {
      return this.ranking[0] || null;
    },

    get bundleAdvantage() {
      return findBundleAdvantage(this.ranking);
    },

    get suggestion() {
      const result = this.comparisonResult;
      if (!result || result.newColors.length === 0 || result.newColors.length > FEW_NEW_COLORS_THRESHOLD) return null;
      return suggestAlternative(this.ranking, this.compareHandle);
    },

    get singleSetOnlyMarkers() {
      if (!this._singleSetOnlyCache) {
        this._singleSetOnlyCache = findSingleSetOnlyMarkers(this.sets);
      }
      return this._singleSetOnlyCache;
    },

    isOwned(handle) {
      return this.ownedHandles.includes(handle);
    },

    toggleOwned(handle) {
      if (handle === this.compareHandle) return;
      this.ownedHandles = this.isOwned(handle)
        ? this.ownedHandles.filter((h) => h !== handle)
        : [...this.ownedHandles, handle];
    },

    selectCompare(handle) {
      if (this.isOwned(handle)) return;
      this.compareHandle = this.compareHandle === handle ? null : handle;
      this.showAlreadyHave = false;
    },

    toggleExplorerSet(handle) {
      this.expandedExplorerHandle = this.expandedExplorerHandle === handle ? null : handle;
    },

    isExplorerExpanded(handle) {
      if (this.explorerSearch.trim()) return this.explorerSetHasMatch(handle);
      return this.expandedExplorerHandle === handle;
    },

    explorerSetHasMatch(handle) {
      const set = this.setsByHandle.get(handle);
      return set ? this.filteredMarkers(set.markers).length > 0 : false;
    },

    get explorerHasAnyMatch() {
      if (!this.explorerSearch.trim()) return true;
      return this.sets.some((set) => this.explorerSetHasMatch(set.handle));
    },

    sortedMarkers(markers) {
      const list = [...markers];
      if (this.explorerSort === 'hue') {
        return list.sort((a, b) => hexToHue(a.hex) - hexToHue(b.hex));
      }
      return list.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true }));
    },

    filteredMarkers(markers) {
      const query = this.explorerSearch.trim().toLowerCase();
      const sorted = this.sortedMarkers(markers);
      if (!query) return sorted;
      return sorted.filter(
        (m) => m.number.toLowerCase().includes(query) || m.name.toLowerCase().includes(query)
      );
    },

    isSingleSetOnly(number) {
      return this.singleSetOnlyMarkers.has(number);
    },

    isLight(hex) {
      return isLightColor(hex);
    },

    markerAriaLabel(marker) {
      return interpolate(this.strings.markerAriaLabel || '{{ number }} {{ name }}', {
        number: marker.number,
        name: marker.name,
      });
    },

    ownerOf(number) {
      const owner = this.ownedSets.find((s) => s.markers.some((m) => m.number === number));
      return owner ? owner.title : '';
    },

    formatMoney(amountCents) {
      const locale = document.documentElement.lang || 'de';
      const currency = this.settings.currencyCode || 'EUR';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountCents / 100);
      } catch (err) {
        return `${(amountCents / 100).toFixed(2)} ${currency}`;
      }
    },

    // --- Übersetzte, interpolierte Anzeige-Texte (dynamisch, daher clientseitig) ---

    get ownedSummaryText() {
      return formatPlural(this.strings.ownedSummary, this.collectionCount);
    },

    get resultHeadlineText() {
      const result = this.comparisonResult;
      if (!result) return '';
      return formatPlural(this.strings.resultNewColors, result.newColors.length);
    },

    get resultPercentText() {
      const result = this.comparisonResult;
      if (!result) return '';
      return interpolate(this.strings.resultPercentOwned, { percent: result.percentOwned });
    },

    alreadyHaveSourceText(number) {
      return interpolate(this.strings.alreadyHaveSource, { set: this.ownerOf(number) });
    },

    get collectionAfterText() {
      const result = this.comparisonResult;
      if (!result) return '';
      return interpolate(this.strings.collectionAfter, {
        before: result.collectionSizeBefore,
        after: result.collectionSizeAfter,
      });
    },

    get suggestionText() {
      const s = this.suggestion;
      if (!s) return '';
      return interpolate(this.strings.fewNewSuggestion, { set: s.set.title, count: s.newCount });
    },

    rankingNewColorsText(entry) {
      return formatPlural(this.strings.rankingNewColors, entry.newCount);
    },

    rankingPriceText(entry) {
      return interpolate(this.strings.rankingPricePerColor, { price: this.formatMoney(entry.pricePerNewColor) });
    },

    get bundleHintText() {
      const advantage = this.bundleAdvantage;
      if (!advantage) return '';
      return interpolate(this.strings.bundleHint, { bundle: advantage.bundle.title, single: advantage.comparedTo.title });
    },

    get progressGapHintText() {
      const gap = this.gapCloser;
      if (!gap) return '';
      return interpolate(this.strings.progressGapHint, { set: gap.set.title });
    },

    async addToCart(set) {
      if (!set || !set.firstVariantId || this.cartLoadingHandle) return;
      this.cartLoadingHandle = set.handle;
      this.cartMessage = '';

      const modalCart = window.theme && window.theme.settings && window.theme.settings.cart_type === 'modal';
      const formData = new FormData();
      formData.append('id', set.firstVariantId);
      formData.append('quantity', 1);
      if (modalCart) {
        formData.append('sections', 'cart-items,cart-footer,cart-item-count');
        formData.append('sections_url', window.location.pathname);
      }

      const config = fetchConfigDefaults('javascript');
      config.body = formData;
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      try {
        const res = await fetch(window.theme.routes.cart_add_url, config);
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || 'cart_add_failed');

        if (modalCart) {
          document.body.dispatchEvent(
            new CustomEvent('shapes:modalcart:afteradditem', {
              bubbles: true,
              detail: { response: data },
            })
          );
        }

        document.dispatchEvent(
          new CustomEvent('lgt:add_to_cart', {
            detail: { set: set.handle, variantId: set.firstVariantId, response: data },
          })
        );

        this.cartMessage = this.strings.cartAdded || '';
      } catch (err) {
        this.cartMessage = this.strings.cartError || '';
      } finally {
        this.cartLoadingHandle = null;
      }
    },

    async copyShareLink() {
      try {
        await navigator.clipboard.writeText(window.location.href);
        this.cartMessage = this.strings.shareLinkCopied || '';
      } catch (err) {
        // Clipboard-API nicht verfügbar – Link bleibt in der Adressleiste sichtbar.
      }
    },
  }));
});
